import type { ISerialize } from '@/objects/ISerialize';
import type { SearchDataType } from './../enums/SearchDataType';
import { formatDateIso } from '@/utilities/dateutils';

export class EventNameDateId implements ISerialize {
    public ID: number;

    public DataType: SearchDataType | null;

    public Date: Date;

    public Description: string | null;

    public Text: string;

    /***
     * @deprecated Use the static FromJsValues method instead of the constructor to get type safety and default values
     */
    public constructor(values: {
        ID?: number;
        DataType?: SearchDataType | null;
        Date?: Date;
        Description?: string | null;
        Text?: string | null;
    }) {
        this.ID = values.ID ?? 0;

        this.DataType = values.DataType ?? 'Event';

        this.Date = values.Date ?? new Date('1970-01-01T00:00:00.000');

        this.Description = values.Description ?? null;

        this.Text = values.Text ?? '';
    }

    public serialize(): string {
        return (
            '{' +
            `"ID": ${JSON.stringify(this.ID)}` +
            ',' +
            `"DataType": ${this.DataType == null ? null : this.DataType == 'Event' ? 1 : this.DataType == 'Race' ? 2 : this.DataType == 'Category' ? 3 : this.DataType == 'Group' ? 4 : this.DataType == 'Station' ? 5 : this.DataType == 'StationRole' ? 6 : this.DataType == 'Participant' ? 7 : this.DataType == 'Contestant' ? 8 : null}` +
            ',' +
            `"Date": ${'"' + formatDateIso(this.Date) + '"'}` +
            ',' +
            `"Description": ${JSON.stringify(this.Description)}` +
            ',' +
            `"Text": ${JSON.stringify(this.Text)}` +
            '}'
        );
    }

    /**
     * Creates an instance of EventNameDateId from the supplied values.
     * Missing values have default values
     * @param values The values you want to set
     * @returns An object instance with the supplied values set
     */
    public static FromJsValues(values: {
        ID?: number;
        DataType?: SearchDataType | null;
        Date?: Date;
        Description?: string | null;
        Text?: string | null;
    }): EventNameDateId {
        return new EventNameDateId(values);
    }

    public static _FromServerJson(json: any): EventNameDateId {
        return new EventNameDateId({
            ID: json.ID,
            DataType:
                json.DataType == null
                    ? null
                    : json.DataType == 1
                      ? 'Event'
                      : json.DataType == 2
                        ? 'Race'
                        : json.DataType == 3
                          ? 'Category'
                          : json.DataType == 4
                            ? 'Group'
                            : json.DataType == 5
                              ? 'Station'
                              : json.DataType == 6
                                ? 'StationRole'
                                : json.DataType == 7
                                  ? 'Participant'
                                  : json.DataType == 8
                                    ? 'Contestant'
                                    : null,
            Date: json.Date instanceof Date ? json.Date : new Date(json.Date),
            Description: json.Description ?? '',
            Text: json.Text ?? '',
        });
    }

    /**
     * Creates an instance of EventNameDateId from another instance of EventNameDateId.
     * @param other The EventNameDateId you want to clone
     * @returns A cloned EventNameDateId
     */
    public static FromOther(other: EventNameDateId): EventNameDateId {
        return EventNameDateId._FromServerJson(JSON.parse(other.serialize()));
    }

    /**
     * Deepclones the object.
     */
    public DeepClone() {
        return EventNameDateId.FromOther(this);
    }

    static NAME_OF_CLASS = 'EventNameDateId';
}
